import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, AlertTriangle, Clock, CheckCircle2, ListChecks, Filter, Inbox, Send,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatDateTime } from "../lib/format";
import { StatCard, Section, EmptyState, Skeleton, Pagination, usePagination } from "../components/UI";

const PRIORITY_CLS = {
  Tinggi: "bg-red-50 text-red-700 border-red-200",
  Sedang: "bg-amber-50 text-amber-700 border-amber-200",
  Rendah: "bg-slate-50 text-slate-700 border-slate-200",
};

const STATUS_CLS = {
  "Open": "bg-blue-50 text-blue-700 border-blue-200",
  "On Process": "bg-amber-50 text-amber-700 border-amber-200",
  "Closed": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const SLA_CLS = {
  "On Track": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Near Breach": "bg-amber-50 text-amber-700 border-amber-200",
  "Breached": "bg-red-50 text-red-700 border-red-200",
  "Resolved": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const formatAge = (h) => {
  if (h == null) return "-";
  if (h < 1) return `${Math.round(h * 60)} mnt`;
  if (h < 48) return `${h.toFixed(1)} jam`;
  return `${(h / 24).toFixed(1)} hari`;
};

export default function TicketListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [box, setBox] = useState("all"); // all | created | assigned
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { box };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const [t, s] = await Promise.all([
        api.get("/tickets", { params }),
        api.get("/tickets/stats"),
      ]);
      setTickets(t.data);
      setStats(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [box, statusFilter, priorityFilter]);

  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(tickets, 10);

  return (
    <div className="space-y-6" data-testid="ticket-list-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Bantuan</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Tiket Kendala</h1>
          <p className="text-slate-500 text-sm mt-1">
            {user.role === "KPPG"
              ? "Semua tiket dari semua user tampil di sini."
              : "Hanya tiket yang Anda buat atau yang ditujukan kepada Anda."}
          </p>
        </div>
        <button onClick={() => navigate("/tickets/new")} className="kedan-btn-primary" data-testid="create-ticket-btn">
          <Plus className="w-4 h-4" /> Buat Laporan Baru
        </button>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={ListChecks} label="Total Tiket" value={stats.total} accent="blue" testid="stat-total" />
          <StatCard icon={Inbox} label="Open" value={stats.open} accent="blue" testid="stat-open" />
          <StatCard icon={Clock} label="On Process" value={stats.on_process} accent="amber" testid="stat-process" />
          <StatCard icon={CheckCircle2} label="Closed" value={stats.closed} accent="emerald" testid="stat-closed" />
          <StatCard icon={AlertTriangle} label="SLA Breached" value={stats.breached} sub={`${stats.near_breach} mendekati breach`} accent="rose" testid="stat-breached" />
        </div>
      )}

      <div className="kedan-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="flex gap-1.5">
            {[
              { v: "all", label: "Semua" },
              { v: "created", label: "Saya Buat", icon: Send },
              { v: "assigned", label: "Ditujukan ke Saya", icon: Inbox },
            ].map((b) => (
              <button key={b.v} onClick={() => setBox(b.v)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors inline-flex items-center gap-1.5 ${box === b.v ? "bg-[#045498] text-white border-[#045498]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}
                data-testid={`box-${b.v}`}>
                {b.icon && <b.icon className="w-3 h-3" />}
                {b.label}
              </button>
            ))}
          </div>
          <div className="ml-2 flex gap-2">
            <select className="kedan-input !w-32 !py-1 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
              <option value="">Semua Status</option>
              <option>Open</option><option>On Process</option><option>Closed</option>
            </select>
            <select className="kedan-input !w-32 !py-1 text-xs" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} data-testid="filter-priority">
              <option value="">Semua Prioritas</option>
              <option>Tinggi</option><option>Sedang</option><option>Rendah</option>
            </select>
          </div>
          <div className="ml-auto text-xs text-slate-500">{tickets.length} tiket</div>
        </div>

        {loading ? (
          <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : tickets.length === 0 ? (
          <EmptyState title="Belum ada laporan" description="Klik 'Buat Bantuan Baru' untuk menyampaikan keluhan atau permintaan." icon={Inbox}
            action={<button onClick={() => navigate("/tickets/new")} className="kedan-btn-primary"><Plus className="w-4 h-4" /> Buat Laporan</button>}
          />
        ) : (<>
          <table className="kedan-table">
            <thead>
              <tr>
                <th>No. Tiket</th>
                <th>Subjek</th>
                <th>Dari → Ke</th>
                <th>Prioritas</th>
                <th>Status</th>
                <th>Age / SLA</th>
                <th></th>
              </tr>
            </thead>
            <tbody data-testid="ticket-table-body">
              {paged.map((t) => (
                <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} className="cursor-pointer" data-testid={`ticket-row-${t.id}`}>
                  <td className="font-mono text-xs">{t.ticket_number}</td>
                  <td>
                    <div className="font-medium text-slate-900">{t.subject}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{formatDateTime(t.created_at)}</div>
                  </td>
                  <td className="text-xs text-slate-600">
                    <div><span className="text-slate-500">Dari:</span> {t.creator_name} ({t.creator_role})</div>
                    <div><span className="text-slate-500">Ke:</span> {t.assignee_name} ({t.assignee_role})</div>
                  </td>
                  <td><span className={`kedan-badge ${PRIORITY_CLS[t.priority]}`}>{t.priority}</span></td>
                  <td><span className={`kedan-badge ${STATUS_CLS[t.status]}`}>{t.status}</span></td>
                  <td>
                    <div className="text-sm font-mono">{formatAge(t.age_hours)}</div>
                    <div className="mt-0.5"><span className={`kedan-badge text-[10px] ${SLA_CLS[t.sla_status]}`}>{t.sla_status}</span></div>
                  </td>
                  <td className="text-right">
                    <span className="text-xs text-[#045498]">Detail →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
        </>)}
      </div>
    </div>
  );
}
